// 3rd party
import axios from 'axios';

// Bernie
import { getErrorString, Logger, SystemEvent, SystemEventLevel } from 'bernie-logger';

// Models
import { Link, PartnerizeTracking } from 'models';

interface PartnerizeTrackingLinkOptions {
  baseUrl: string;
  camRef: string;
  pubRef?: string;
  adRef?: string;
  creativeRef?: string;
  destination?: string;
}

interface PartnerizeTrackingLinksOptions {
  tracking: PartnerizeTracking;
  links: Link[];
  logger?: Logger;
}

function getPartnerizeTrackingLink({
  baseUrl,
  camRef,
  pubRef,
  adRef,
  creativeRef,
  destination,
}: PartnerizeTrackingLinkOptions): string {
  if (baseUrl && camRef) {
    const camRefPath = `/camref:${camRef}`;
    const pubRefPath = pubRef ? `/pubref:${pubRef}` : '';
    const adRefPath = adRef ? `/adref:${adRef}` : '';
    const creativeRefPath = creativeRef ? `/creativeref:${creativeRef}` : '';
    const destinationPath = destination ? `/destination:${destination}` : '';

    return `${baseUrl}${camRefPath}${pubRefPath}${adRefPath}${creativeRefPath}${destinationPath}`;
  }
}

async function getPartnerizeTrackingLinks(options: PartnerizeTrackingLinksOptions): Promise<Link[]> {
  const { tracking, links, logger } = options;
  const { baseUrl, camRef, pubRef, adRef, creativeRef } = tracking;

  if (!baseUrl || !camRef || !links.length || links.some(({ url }) => !url)) {
    logger?.logEvent(new SystemEvent(SystemEventLevel.WARN, 'PARTNERIZE_TRACKING_LINKS_INVALID'), { tracking, links });

    return;
  }

  logger?.logEvent(new SystemEvent(SystemEventLevel.INFO, 'PARTNERIZE_TRACKING_LINKS_VALID'), { tracking, links });

  const camRefPathParam = `/camref:${camRef}`;
  const pubRefPathParam = pubRef ? `/pubref:${pubRef}` : '';
  const adRefPathParam = adRef ? `/adref:${adRef}` : '';
  const creativeRefPathParam = creativeRef ? `/creativeref:${creativeRef}` : '';

  const trackingLinks = links.map(function getTrackingLink(link) {
    const { url } = link;

    const destinationPathParam = `/destination:${url}`;

    const trackingLinkUrl = `${baseUrl}${camRefPathParam}${pubRefPathParam}${adRefPathParam}${creativeRefPathParam}${destinationPathParam}`;

    const trackingLink: Link = {
      ...link,
      url: trackingLinkUrl,
    };

    return trackingLink;
  });

  logger?.logEvent(
    new SystemEvent(SystemEventLevel.INFO, 'PARTNERIZE_TRACKING_LINKS_GENERATED'),
    { tracking, links },
    { trackingLinks },
  );

  return trackingLinks;
}

async function getAtlasPartnerizeTrackingLinks(options: PartnerizeTrackingLinksOptions): Promise<Link[]> {
  const { tracking, links, logger } = options;
  const { camRef, pubRef, creativeRef } = tracking;

  if (!camRef || !links.length || links.some(({ label, url }) => !label || !url)) {
    logger?.logEvent(new SystemEvent(SystemEventLevel.WARN, 'ATLAS_PARTNERIZE_TRACKING_LINKS_INVALID'), {
      tracking,
      links,
    });

    return;
  }

  logger?.logEvent(new SystemEvent(SystemEventLevel.INFO, 'ATLAS_PARTNERIZE_TRACKING_LINKS_VALID'), {
    tracking,
    links,
  });

  interface AtlasPartnerizeUrlsRequestData {
    camref: string;
    pubref?: string;
    creativeref?: string;
    landingPages: Link[];
  }

  interface AtlasPartnerizeUrlsResponseData {
    affiliateUrls: Link[];
  }

  const requestData: AtlasPartnerizeUrlsRequestData = {
    camref: camRef,
    pubref: pubRef,
    creativeref: creativeRef,
    landingPages: links,
  };

  try {
    const response = await axios.post<AtlasPartnerizeUrlsResponseData>('/api/atlas/urls/pz', requestData);

    const { data: responseData } = response;
    const { affiliateUrls: trackingLinks } = responseData;

    logger?.logEvent(
      new SystemEvent(SystemEventLevel.INFO, 'ATLAS_PARTNERIZE_TRACKING_LINKS_GENERATED'),
      { tracking, links },
      { trackingLinks },
      { status: response.status, message: response.statusText },
    );

    return trackingLinks;
  } catch (error) {
    logger?.logEvent(
      new SystemEvent(SystemEventLevel.ERROR, 'ATLAS_PARTNERIZE_TRACKING_LINKS_ERROR'),
      { tracking, links },
      getErrorString(error),
    );

    return;
  }
}

export { getPartnerizeTrackingLink, getPartnerizeTrackingLinks, getAtlasPartnerizeTrackingLinks };
